import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaEdit, FaTrash, FaTag, FaInbox } from 'react-icons/fa';
import { getLeadOrigins, deleteLeadOrigin } from './leadOriginService';
import DashboardLayout from '../../layouts/DashboardLayout';
import Modal from '../../components/shared/Modal';
import Pagination from '../../components/shared/Pagination'; 
import {  ResponsiveHeader } from '../../components/styled/CommonStyles';
import {
  GridContainer,
  Card,
  CardHeader,
  LeadName,
  CardContent,
  ActionIconsContainer,
  ActionButton,
  EmptyState,
  LoadingCard
} from '../../components/styled/GridComponents';

const LeadOriginList = () => {
  const [leadOrigins, setLeadOrigins] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [originToDelete, setOriginToDelete] = useState(null);
  const [searchQuery, setSearchQuery] = useState(''); 
  const [filteredOrigins, setFilteredOrigins] = useState([]); 
  const [currentPage, setCurrentPage] = useState(1); 
  const [itemsPerPage] = useState(5); 
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);


  useEffect(() => {
    const fetchLeadOrigins = async () => {
      setLoading(true);
      try{
        const response = await getLeadOrigins();
        setLeadOrigins(response.data);
        setFilteredOrigins(response.data);
      }catch(err){
        setError('Failed to load lead origins');
        console.error('Error fetching lead origins:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchLeadOrigins();
  }, []);

  useEffect(() => {
    const filtered = leadOrigins.filter((origin) =>
      origin.origin_name.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredOrigins(filtered);
    setCurrentPage(1);
  }, [searchQuery, leadOrigins]);

  const handleDelete = async () => {
    await deleteLeadOrigin(originToDelete);
    setDeleteModalOpen(false);
    setOriginToDelete(null);
    const updatedOrigins = leadOrigins.filter((origin) => origin.id !== originToDelete);
    setLeadOrigins(updatedOrigins);
    setFilteredOrigins(updatedOrigins);
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  // const currentOrigins = filteredOrigins.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const currentOrigins = filteredOrigins.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <DashboardLayout>
      <div>
      <ResponsiveHeader
        title="Lead Origins"
        onSearch={setSearchQuery}
        onAdd={() => navigate('/leads/origins/new')}
      />
      <GridContainer>
        {loading ? (
          <>
            {[...Array(4)].map((_, index) => (
              <LoadingCard key={index}>
                <CardHeader>
                  <div style={{ width: '60%', height: '24px', background: '#333' }} />
                </CardHeader>
                <CardContent>
                  <div style={{ width: '40%', height: '18px', background: '#333' }} />
                </CardContent>
              </LoadingCard>
            ))}
          </>
        ) : currentOrigins.length === 0 ? (
          <EmptyState>
            <FaInbox size={48} />
            <p>No lead origins found. Try adjusting your search or add a new origin.</p>
          </EmptyState>
        ) : (
          currentOrigins.map((origin) => (
            <Card key={origin.id}>
              <CardHeader>
                <LeadName>
                  <FaTag style={{ marginRight: '8px' }} />
                  {origin.origin_name}
                </LeadName>
              </CardHeader>

              <ActionIconsContainer>
                <ActionButton
                  onClick={() => navigate(`/leads/origins/edit/${origin.id}`)}
                  title="Edit Origin"
                >
                  <FaEdit />
                </ActionButton>
                <ActionButton
                  onClick={() => {
                    setOriginToDelete(origin.id);
                    setDeleteModalOpen(true);
                  }}
                  title="Delete Origin"
                >
                  <FaTrash />
                </ActionButton>
              </ActionIconsContainer>
            </Card>
          ))
        )}
      </GridContainer>

      {!loading && filteredOrigins.length > itemsPerPage && (
        <Pagination
          itemsPerPage={itemsPerPage}
          totalItems={filteredOrigins.length}
          paginate={(pageNumber) => setCurrentPage(pageNumber)}
          currentPage={currentPage}
        />
      )}

      {deleteModalOpen && (
        <Modal
          message="Are you sure you want to delete this lead origin?"
          onConfirm={handleDelete}
          onCancel={() => {
            setDeleteModalOpen(false);
            setOriginToDelete(null);
          }}
          title="Confirm Delete"
          isOpen={deleteModalOpen}
        />
      )}
      </div>
    </DashboardLayout>
  );
};

export default LeadOriginList;
